import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Clock, CheckCircle2, FileText, Mail, Users } from 'lucide-react';
import { format } from 'date-fns';
import { useParams } from 'react-router-dom';
import type { Listings } from '@/types/PropertyListings';
import Helper from '@/lib/helper';
import { toast } from 'sonner';
import api from '@/lib/axiosInstance';
import type { ListingDocuments } from '@/types/PropertyListingDocuments';

interface TimelineProps {
    listing: Listings;
}

interface TimelineEvent {
    id: number;
    stageId: number;
    stageName: string;
    remarks?: string;
    createdAt: string;
    createdBy: number;
    agent?: {
        id: number;
        firstName: string;
        lastName: string;
        avatar?: string;
        renNo: string;
    };
    document?: ListingDocuments;
}

const Timeline: React.FC<TimelineProps> = ({
    listing
}) => {
    const { listingId } = useParams<{ listingId: string }>();
    const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    useEffect(() => {
        fetchTimeline();
    }, [listingId]);

    const fetchTimeline = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/listings/${listingId}/timeline`);
            if (!response.data.status) {
                throw new Error(response.data.error);
            } 
            const logs = response.data.logs;
            setTimeline(logs);
        } catch (err) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        } finally {
            setLoading(false);
        }
    };

    const getStageIcon = (stageId: number, isLatest: boolean) => {
        if (isLatest && stageId !== 3) {
            return (
                <div className="relative">
                    <Clock size={20} className="text-blue-600" />
                    <div className="absolute -inset-1 bg-blue-400/20 rounded-full animate-pulse" />
                </div>
            );
        }
        return <CheckCircle2 size={20} className="text-emerald-600" />;
    };

    const getStageColor = (stageId: number, isLatest: boolean) => {
        if (isLatest && stageId !== 3) {
            return 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 border-blue-200/50';
        }
        return 'bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-700 border-emerald-200/50';
    };

    const getSignerStatusBadge = (status: string) => {
        const statusMap: Record<string, { color: string; label: string }> = {
            created: { color: 'bg-slate-50 text-slate-600 border-slate-200/50', label: 'Created' },
            sent: { color: 'bg-blue-50 text-blue-600 border-blue-200/50', label: 'Sent' },
            delivered: { color: 'bg-indigo-50 text-indigo-600 border-indigo-200/50', label: 'Delivered' },
            signed: { color: 'bg-emerald-50 text-emerald-600 border-emerald-200/50', label: 'Signed' },
            completed: { color: 'bg-teal-50 text-teal-600 border-teal-200/50', label: 'Completed' },
            declined: { color: 'bg-rose-50 text-rose-600 border-rose-200/50', label: 'Declined' },
            authentication_failed: { color: 'bg-red-50 text-red-600 border-red-200/50', label: 'Auth Failed' },
        };

        const config = statusMap[status] || statusMap.created;

        return (
            <Badge className={`${config.color} border text-xs font-medium`}>
                {config.label}
            </Badge>
        );
    };

    const SignatureProgressContent = ({ document }: { document: ListingDocuments }) => (
        <div className="space-y-3">
            {document.signers
                .sort((a, b) => (a.id) - (b.id))
                .map((signer, signerIndex) => {
                    const isCompleted = signer.status === 'completed' || signer.status === 'signed';
                    const isLastSigner = signerIndex === document.signers.length - 1;
                    
                    return (
                        <div key={signer.id} className="relative flex gap-3">
                            <div className="relative z-10 flex-shrink-0">
                                <div className={`flex items-center justify-center w-6 h-6 rounded-full border-2 transition-all duration-300 ${
                                    isCompleted 
                                        ? 'bg-gradient-to-br from-emerald-100 to-teal-100 border-emerald-400 shadow-sm' 
                                        : 'bg-white border-slate-300 shadow-sm'
                                }`}>
                                    {isCompleted ? (
                                        <CheckCircle2 size={12} className="text-emerald-600" />
                                    ) : (
                                        <Clock size={12} className="text-slate-400" />
                                    )}
                                </div>
                            </div>

                            <div className={`flex-1 bg-gradient-to-br from-slate-50/50 to-white rounded-lg p-3 border border-slate-100 transition-all duration-300 ${!isLastSigner ? 'mb-1' : ''}`}>
                                <div className="flex items-start justify-between gap-2 mb-2">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-semibold text-slate-700 truncate">
                                            {signer.name}
                                        </p>
                                        <p className="text-xs text-slate-500 truncate">
                                            {signer.email}
                                        </p>
                                    </div>
                                    {getSignerStatusBadge(signer.status)}
                                </div>
                                
                                <div className="mt-2 space-y-1.5">
                                    {signer.sentAt && (
                                        <div className="flex items-center gap-1.5 text-xs text-slate-500">
                                            <div className="w-4 h-4 rounded bg-blue-100 flex items-center justify-center">
                                                <Mail size={8} className="text-blue-600" />
                                            </div>
                                            <span className="font-medium">Sent:</span>
                                            <span>{format(new Date(signer.sentAt), 'MMM dd, hh:mm a')}</span>
                                        </div>
                                    )}
                                    {signer.completedAt && (
                                        <div className="flex items-center gap-1.5 text-xs text-emerald-600">
                                            <div className="w-4 h-4 rounded bg-emerald-100 flex items-center justify-center">
                                                <CheckCircle2 size={8} className="text-emerald-600" />
                                            </div>
                                            <span className="font-medium">Signed:</span>
                                            <span>{format(new Date(signer.completedAt), 'MMM dd, hh:mm a')}</span>
                                        </div>
                                    )}
                                    {!signer.completedAt && signer.status !== 'completed' && signer.status !== 'signed' && (
                                        <div className="flex items-center gap-1.5 text-xs text-amber-600">
                                            <div className="w-4 h-4 rounded bg-amber-100 flex items-center justify-center">
                                                <Clock size={8} className="text-amber-600" />
                                            </div>
                                            <span className="font-medium">Awaiting signature</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
        </div>
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <div className="relative">
                    <Clock className="animate-spin text-blue-500" size={40} />
                    <div className="absolute inset-0 bg-blue-400/20 rounded-full animate-ping" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <Card className="border-rose-200/50 bg-gradient-to-br from-rose-50/50 to-red-50/30">
                <CardContent className="py-16">
                    <div className="text-center">
                        <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-4">
                            <Clock className="text-rose-600" size={24} />
                        </div>
                        <p className="text-sm text-rose-600 font-medium">{error}</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (timeline.length === 0) {
        return (
            <Card className="border-slate-200/50 bg-gradient-to-br from-slate-50/50 to-gray-50/30">
                <CardContent className="py-16">
                    <div className="text-center">
                        <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                            <Clock className="text-slate-400" size={32} />
                        </div>
                        <p className="text-sm text-slate-500 font-medium">No timeline events yet</p>
                        <p className="text-xs text-slate-400 mt-1">Events will appear here as they occur</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-6">
            <Card className="border-slate-200/50 bg-gradient-to-br from-white to-slate-50/30 shadow-sm">
                <CardHeader className="border-b border-slate-100/50 bg-gradient-to-r from-slate-50/50 to-transparent">
                    <CardTitle className="text-xl font-semibold flex items-center gap-2 text-slate-700">
                         Timeline
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-6 md:pt-8 pb-6 px-4 md:px-6">
                    <div className="relative">
                        <div className="absolute left-6 md:left-8 top-0 bottom-0 w-px bg-gradient-to-b from-slate-200 via-slate-300 to-slate-200" />

                        <div className="space-y-6 md:space-y-8">
                            {timeline.map((event, index) => {
                                const isLatest = index === 0;
                                const isAgreementStage = event.stageId === 4;
                                const hasDocument = event.document && event.document.signers && event.document.signers.length > 0;
                                const completedSigners = event.document?.signers.filter(s => s.status === 'completed' || s.status === 'signed').length || 0;
                                const totalSigners = event.document?.signers.length || 0;

                                return (
                                    <div key={event.id} className="relative flex gap-3 md:gap-5 group">
                                        <div className="relative z-10 flex-shrink-0">
                                            <div className={`flex items-center justify-center w-12 h-12 md:w-16 md:h-16 rounded-full md:rounded-xl bg-gradient-to-br shadow-sm transition-all duration-300 ${
                                                isLatest && event.stageId !== 3 
                                                    ? 'from-blue-50 to-indigo-50 border-2 border-blue-200/50' 
                                                    : 'from-emerald-50 to-teal-50 border-2 border-emerald-200/50'
                                            }`}>
                                                {getStageIcon(event.stageId, isLatest)}
                                            </div>
                                        </div>

                                        <div className="flex-1 pb-2 min-w-0">
                                            <div className="bg-white rounded-xl p-4 md:p-5 border border-slate-200/50 shadow-sm transition-all duration-300">
                                                <div className="flex items-start justify-between gap-3 mb-4">
                                                    <div className="space-y-2 min-w-0 flex-1">
                                                        <Badge className={`${getStageColor(event.stageId, isLatest)} border shadow-sm`}>
                                                            {event.stageName}
                                                        </Badge>
                                                        <p className="text-xs text-slate-500 font-medium flex items-center gap-1.5">
                                                            <Clock size={12} className="text-slate-400 flex-shrink-0" />
                                                            <span className="truncate">{format(new Date(event.createdAt), 'MMM dd, yyyy • hh:mm a')}</span>
                                                        </p>
                                                    </div>
                                                </div>

                                                {event.remarks && (
                                                    <div className="mb-4 p-3 rounded-lg bg-slate-50/50 border border-slate-100">
                                                        <p className="text-sm text-slate-700 leading-relaxed break-words">
                                                            {event.remarks}
                                                        </p>
                                                    </div>
                                                )}

                                                {event.agent && (
                                                    <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                                                        <Avatar className="h-9 w-9 ring-2 ring-white shadow-sm flex-shrink-0">
                                                            <AvatarImage 
                                                                src={event.agent.avatar ? `${import.meta.env.VITE_AWS_S3}/${event.agent.avatar}` : undefined} 
                                                            />
                                                            <AvatarFallback className="text-xs bg-gradient-to-br from-blue-500 to-indigo-500 text-white font-semibold">
                                                                {event.agent.firstName?.[0]}{event.agent.lastName?.[0]}
                                                            </AvatarFallback>
                                                        </Avatar>
                                                        <div className="min-w-0 flex-1">
                                                            <p className="text-xs font-semibold text-slate-700 truncate">
                                                                {event.agent.firstName} {event.agent.lastName}
                                                            </p>
                                                            <p className="text-xs text-slate-500 truncate">
                                                                REN {event.agent.renNo}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Signature Timeline - Mobile & Desktop */}
                                                {isAgreementStage && hasDocument && event.document && (
                                                    <>
                                                        {/* Mobile: Button/Badge with Dialog */}
                                                        <div className="mt-4 pt-4 border-t border-slate-100 md:hidden">
                                                            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                                                                <DialogTrigger asChild>
                                                                    <Button 
                                                                        variant="outline" 
                                                                        className="w-full justify-between bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200/50 hover:from-violet-100 hover:to-purple-100"
                                                                    >
                                                                        <div className="flex items-center gap-2">
                                                                            <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                                                                                <Users size={12} className="text-white" />
                                                                            </div>
                                                                            <span className="text-xs font-semibold text-slate-700">View Signers</span>
                                                                        </div>
                                                                        <Badge className="bg-slate-100 text-slate-600 border-slate-200 flex-shrink-0">
                                                                            {completedSigners}/{totalSigners}
                                                                        </Badge>
                                                                    </Button>
                                                                </DialogTrigger>
                                                                <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
                                                                    <DialogHeader>
                                                                        <DialogTitle className="flex items-center gap-2">
                                                                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
                                                                                <FileText size={16} className="text-white" />
                                                                            </div>
                                                                            Signature Progress
                                                                        </DialogTitle>
                                                                        <p className="text-sm text-slate-500">
                                                                            {completedSigners} of {totalSigners} signers completed
                                                                        </p>
                                                                    </DialogHeader>
                                                                    <div className="mt-4 relative pl-7">
                                                                        <div className="absolute left-3 top-0 bottom-0 w-px bg-gradient-to-b from-slate-200 to-transparent" />
                                                                        <SignatureProgressContent document={event.document} />
                                                                    </div>
                                                                </DialogContent>
                                                            </Dialog>
                                                        </div>

                                                        {/* Desktop: Inline display */}
                                                        <div className="mt-5 pt-5 border-t border-slate-100 hidden md:block">
                                                            <div className="flex items-center gap-2 mb-4">
                                                                <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
                                                                    <FileText size={12} className="text-white" />
                                                                </div>
                                                                <p className="text-xs font-semibold text-slate-700">Signature Progress</p>
                                                                <Badge className="ml-auto bg-slate-100 text-slate-600 border-slate-200">
                                                                    {completedSigners}/{totalSigners}
                                                                </Badge>
                                                            </div>
                                                            <div className="relative pl-7">
                                                                <div className="absolute left-3 top-0 bottom-0 w-px bg-gradient-to-b from-slate-200 to-transparent" />
                                                                <SignatureProgressContent document={event.document} />
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default Timeline;